import { useState } from "react";
import { Zap } from "lucide-react";
import { KitModal as Modal } from "@/app/routes/dashboard/components/kit/Modal";
import { Button } from "@/components/form/Button";
import { KitSegmentedControl } from "../../components/kit/SegmentedControl";
import {
  CALENDAR_MEDIA_TYPES,
  CALENDAR_VIDEO_LENGTHS,
  MEDIA_TYPE_LABEL,
  SLOT_ASPECT_RATIOS_BY_MEDIA_TYPE,
  VIDEO_LENGTH_LABEL,
  orientationForAspectRatio,
  type AspectRatio,
  type CalendarMediaType,
  type CalendarPlatform,
  type CalendarVideoLength,
} from "@/types/contentCalendar.type";
import { Field, TextInput } from "./fields";
import { PlatformPicker } from "./PlatformPicker";
import { KitStepper } from "../../components/kit/Switch";
import { MAX_SLIDES } from "@/types/scheduling.type";
import { useCreateDirectSlot } from "../api/useContentCalendar";
import { MONTH_NAMES, toUtcIsoDay } from "../calendar.utils";

/** A row of mutually exclusive options: the SegmentedControl, lens on the pick (style B). */
function ChipRow<T extends string>({
  options,
  value,
  onChange,
  labelFor,
  label,
}: {
  options: readonly T[];
  value: T;
  onChange: (next: T) => void;
  labelFor: (option: T) => string;
  label: string;
}) {
  return (
    <KitSegmentedControl<T>
      variant="lens"
      ariaLabel={label}
      options={options.map((option) => ({ label: labelFor(option), value: option }))}
      value={value}
      onChange={onChange}
      className="self-start"
    />
  );
}

/** Today, as the YYYY-MM-DD a date input wants. UTC, like every calendar day. */
function todayInputValue(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Drop ready-made content onto a day — the "we made this yesterday, it goes out
 * tomorrow" path.
 *
 * It exists because the pipeline is the wrong shape for a trend: by the time
 * ideas, a client review and a production brief have run, the moment is gone.
 * So this creates the post already signed off, with no ideas and no brief, and
 * the client portal never shows it. What it does share with every other post is
 * everything downstream — the same upload, the same platform validation, the
 * same publish dispatcher.
 *
 * The date is deliberately free (not bound to the month being viewed): someone
 * reaching for this is reacting to something, and making them navigate to the
 * right month first would be ceremony.
 */
export default function QuickPostModal({
  brandId,
  defaultDay,
  onCreated,
  onClose,
}: {
  brandId: string;
  /** Pre-fill the date when the operator started from a specific day. */
  defaultDay?: { year: number; month: number; day: number };
  /** Hand back the new slot so the caller can open its workroom. */
  onCreated: (slotId: string, planId: string) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(
    defaultDay
      ? toUtcIsoDay(defaultDay.year, defaultDay.month, defaultDay.day).slice(0, 10)
      : todayInputValue(),
  );
  const [mediaType, setMediaType] = useState<CalendarMediaType>("VIDEO");
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>("9:16");
  const [videoLength, setVideoLength] = useState<CalendarVideoLength>("SHORT_FORM");
  const [channels, setChannels] = useState<CalendarPlatform[]>([]);
  const [slideCount, setSlideCount] = useState(1);

  const create = useCreateDirectSlot(brandId);
  const busy = create.isPending;
  const day = new Date(`${date}T00:00:00Z`);
  const validDate = !Number.isNaN(day.getTime());
  const canSubmit = title.trim().length > 0 && validDate && !busy;

  const submit = () => {
    if (!canSubmit) return;
    create.mutate(
      {
        title: title.trim(),
        date: `${date}T00:00:00.000Z`,
        mediaType,
        aspectRatio,
        orientation: orientationForAspectRatio(aspectRatio),
        // A static never carries a length band — the server enforces it too.
        videoLength: mediaType === "VIDEO" ? videoLength : null,
        channels,
        slideCount: mediaType === "STATIC" ? slideCount : null,
      },
      {
        onSuccess: (result) => {
          onCreated(result.slot.id, result.planId);
          onClose();
        },
      },
    );
  };

  const dayLabel = validDate
    ? `${day.getUTCDate()} ${MONTH_NAMES[day.getUTCMonth()]} ${day.getUTCFullYear()}`
    : null;

  return (
    <Modal isOpen onOpenChange={(open) => !open && onClose()}>
      <div className="flex flex-col gap-4">
        <header className="flex flex-col gap-1.5">
          <h2 className="font-section text-[20px] font-extrabold leading-[1.1] tracking-[0.015em] text-co-text">
            Quick post
          </h2>
          <p className="text-[13px] leading-[1.55] text-co-muted">
            For content that already exists. It skips ideas, briefs and client review entirely
            &mdash; add the media, pick a time, and it publishes like any other post. Your client
            never sees it on their calendar.
          </p>
        </header>

        <Field label="What is it?" htmlFor="quick-post-title">
          <TextInput
            id="quick-post-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Studio dance trend — the one with the coffee cups"
            disabled={busy}
            autoFocus
          />
        </Field>

        <Field
          label="Goes out on"
          htmlFor="quick-post-date"
          hint={dayLabel ? `${dayLabel}. You pick the exact time when you schedule it.` : undefined}
        >
          <TextInput
            id="quick-post-date"
            type="date"
            value={date}
            min={todayInputValue()}
            onChange={(e) => setDate(e.target.value)}
            disabled={busy}
          />
        </Field>

        <Field label="Format" hint="This is checked against the file you upload.">
          <div className="flex flex-col gap-2">
            <ChipRow
              options={CALENDAR_MEDIA_TYPES}
              value={mediaType}
              onChange={(next) => {
                setMediaType(next);
                setAspectRatio(next === "STATIC" ? "4:5" : "9:16");
              }}
              labelFor={(m) => MEDIA_TYPE_LABEL[m]}
              label="Media"
            />
            <ChipRow
              options={SLOT_ASPECT_RATIOS_BY_MEDIA_TYPE[mediaType]}
              value={aspectRatio}
              onChange={setAspectRatio}
              labelFor={(ratio) => ratio}
              label="Canvas"
            />
            {mediaType === "VIDEO" && (
              <ChipRow
                options={CALENDAR_VIDEO_LENGTHS}
                value={videoLength}
                onChange={setVideoLength}
                labelFor={(v) => VIDEO_LENGTH_LABEL[v]}
                label="Length"
              />
            )}
          </div>
        </Field>

        {mediaType === "STATIC" && (
          <Field label="Slides" hint="Two or more post as a carousel. You can add or remove slides later.">
            <KitStepper label="Slides" value={slideCount} min={1} max={MAX_SLIDES} onChange={setSlideCount} disabled={busy} />
          </Field>
        )}

        <Field
          label="Where it posts"
          hint="You can change these later, before it goes out."
        >
          <PlatformPicker value={channels} onChange={setChannels} />
        </Field>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button variant="ghost" size="small" onPress={onClose} isDisabled={busy}>
            Cancel
          </Button>
          <Button
            size="small"
            icon={Zap}
            loading={busy}
            isDisabled={!canSubmit}
            onPress={submit}
          >
            Add to calendar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
